import { motion } from 'framer-motion'
import {
  MessageCircle,
  Users,
  Fingerprint,
  Stethoscope,
  Scale,
  UserSearch,
  GraduationCap,
  TrendingUp,
  Check,
  Clock,
} from 'lucide-react'
import { cn } from '@personic/shared'

export const PRODUCTS = [
  {
    id: 'engage',
    name: 'Engage',
    description: 'Real-time personality insights for customer interactions',
    icon: MessageCircle,
    available: true,
  },
  {
    id: 'coach',
    name: 'Coach',
    description: 'AI-powered personal development and coaching',
    icon: Users,
    available: true,
  },
  {
    id: 'identity',
    name: 'Identity',
    description: 'Behavioral authentication and verification',
    icon: Fingerprint,
    available: false,
    comingSoon: true,
  },
  {
    id: 'clinical',
    name: 'Clinical',
    description: 'Mental health assessment and support tools',
    icon: Stethoscope,
    available: false,
    comingSoon: true,
  },
  {
    id: 'legal',
    name: 'Legal',
    description: 'Behavioral analysis for legal proceedings',
    icon: Scale,
    available: false,
    comingSoon: true,
  },
  {
    id: 'talent',
    name: 'Talent',
    description: 'Hiring and team-building insights',
    icon: UserSearch,
    available: false,
    comingSoon: true,
  },
  {
    id: 'education',
    name: 'Education',
    description: 'Personalized learning experiences',
    icon: GraduationCap,
    available: false,
    comingSoon: true,
  },
  {
    id: 'finance',
    name: 'Finance',
    description: 'Behavioral finance and risk assessment',
    icon: TrendingUp,
    available: false,
    comingSoon: true,
  },
] as const

export type ProductId = typeof PRODUCTS[number]['id']

interface ProductSelectorProps {
  selectedProducts: string[]
  onChange: (products: string[]) => void
  className?: string
}

export function ProductSelector({
  selectedProducts,
  onChange,
  className,
}: ProductSelectorProps) {
  const handleToggle = (productId: string, available: boolean) => {
    if (!available) return

    if (selectedProducts.includes(productId)) {
      onChange(selectedProducts.filter((id) => id !== productId))
    } else {
      onChange([...selectedProducts, productId])
    }
  }

  return (
    <div className={cn('grid grid-cols-1 md:grid-cols-2 gap-4', className)}>
      {PRODUCTS.map((product) => {
        const Icon = product.icon
        const isSelected = selectedProducts.includes(product.id)
        const isAvailable = product.available

        return (
          <motion.button
            key={product.id}
            type="button"
            onClick={() => handleToggle(product.id, product.available)}
            disabled={!isAvailable}
            whileHover={isAvailable ? { scale: 1.02 } : undefined}
            whileTap={isAvailable ? { scale: 0.98 } : undefined}
            className={cn(
              'relative flex items-start p-4 rounded-xl text-left',
              'border-2 transition-all duration-200',
              'focus:outline-none focus:ring-2 focus:ring-copper-400 focus:ring-offset-2',
              isAvailable
                ? isSelected
                  ? 'bg-copper-50 border-copper-500'
                  : 'bg-white border-warm-200 hover:border-copper-300'
                : 'bg-warm-50 border-warm-200 cursor-not-allowed opacity-60'
            )}
          >
            {/* Selection Indicator */}
            {isAvailable && (
              <div
                className={cn(
                  'absolute top-3 right-3 w-5 h-5 rounded-full flex items-center justify-center',
                  'border-2 transition-all duration-200',
                  isSelected
                    ? 'bg-copper-500 border-copper-500'
                    : 'bg-white border-warm-300'
                )}
              >
                {isSelected && <Check className="w-3 h-3 text-white" />}
              </div>
            )}

            {/* Coming Soon Badge */}
            {'comingSoon' in product && product.comingSoon && (
              <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 bg-midnight-100 rounded-full">
                <Clock className="w-3 h-3 text-midnight-500" />
                <span className="text-xs font-heading text-midnight-600">
                  Coming Soon
                </span>
              </div>
            )}

            {/* Icon */}
            <div
              className={cn(
                'w-12 h-12 rounded-xl flex items-center justify-center mr-4 flex-shrink-0',
                isAvailable
                  ? isSelected
                    ? 'bg-copper-500 text-white'
                    : 'bg-midnight-100 text-midnight-600'
                  : 'bg-warm-200 text-warm-400'
              )}
            >
              <Icon className="w-6 h-6" />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0 pr-6">
              <h3
                className={cn(
                  'font-heading font-semibold mb-1',
                  isAvailable
                    ? isSelected
                      ? 'text-copper-700'
                      : 'text-midnight-900'
                    : 'text-warm-500'
                )}
              >
                {product.name}
              </h3>
              <p
                className={cn(
                  'text-sm',
                  isAvailable ? 'text-warm-600' : 'text-warm-400'
                )}
              >
                {product.description}
              </p>
            </div>
          </motion.button>
        )
      })}
    </div>
  )
}
